import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Snackbar, Stack, Typography } from '@mui/material';
import { useMockPortal } from '../mock/useMockPortal';
import { DOCUMENT_CATEGORY_LABELS } from '../mock/store';
import type { DocumentCategory, PortalDocumentItem } from '../mock/types';

function downloadSample(doc: PortalDocumentItem): void {
  const body = [
    'P.E.O. Portal — sample document',
    '',
    doc.title,
    doc.description,
    '',
    'This file is a preview placeholder and is not an official document.',
  ].join('\n');
  const blob = new Blob([body], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.fileName;
  a.click();
  // Defer revoke so the browser can start the download first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const CATEGORY_ORDER: DocumentCategory[] = [
  'tax',
  'payment_coupons',
  'statements',
  'welcome',
];

const LoanDocuments: React.FC = () => {
  const { documents } = useMockPortal();
  const [toast, setToast] = useState<string | null>(null);

  const byCategory = useMemo(() => {
    const map = new Map<DocumentCategory, PortalDocumentItem[]>();
    CATEGORY_ORDER.forEach((c) => map.set(c, []));
    documents.forEach((d) => {
      const list = map.get(d.category) || [];
      list.push(d);
      map.set(d.category, list);
    });
    return map;
  }, [documents]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Documents
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Download tax forms, payment coupons, and statements. Files are sample
        placeholders.
      </Typography>

      <Stack spacing={3}>
        {CATEGORY_ORDER.map((category) => {
          const items = byCategory.get(category) || [];
          if (!items.length) return null;
          return (
            <Box key={category}>
              <Typography variant="h6" gutterBottom>
                {DOCUMENT_CATEGORY_LABELS[category]}
              </Typography>
              <Stack spacing={1.5}>
                {items.map((doc) => (
                  <Paper key={doc.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1.5}
                    >
                      <Box>
                        <Typography fontWeight={600}>{doc.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {doc.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          downloadSample(doc);
                          setToast(`Downloaded ${doc.fileName}`);
                        }}
                      >
                        Download
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
};

export default LoanDocuments;
